// src/services/export/ExportService.ts
// Servicio orquestador de exportación PTEL

import type {
  ExportOptions,
  ExportResult,
  ExportFormat,
  ProcessedRecord,
} from '@/types/export.types';

/**
 * Servicio principal de exportación
 * Orquesta la generación de archivos en múltiples formatos
 */
export class ExportService {
  /**
   * Exporta datos en todos los formatos seleccionados
   */
  static async exportAll(options: ExportOptions): Promise<ExportResult[]> {
    const results: ExportResult[] = [];

    for (const formato of options.formatos) {
      try {
        const result = await this.exportSingle(formato, options);
        results.push(result);
        // AFOTOT-a (DOP-6 a): el CSV lleva al lado su fichero de aforos y albergue.
        if (formato === 'csv' && result.success) {
          const { CSVExporter } = await import('./CSVExporter');
          const base = this.generateFilename(options.metadata.archivoOrigen);
          const aforos = CSVExporter.exportAforos(options, `${base}_aforos_albergue.csv`);
          if (aforos) results.push(aforos);
          // AFOTOT-a3b (DOP-2 a): y su resumen con los totales, `…_aforos_resumen.csv`.
          const resumen = CSVExporter.exportAforosResumen(options, `${base}_aforos_resumen.csv`);
          if (resumen) results.push(resumen);
          // ALBRTA-b2d-1 (DOP-1 a): y los alojamientos del registro que el plan no recoge.
          const noRecogidos = CSVExporter.exportNoRecogidosRTA(options, `${base}_alojamientos_no_recogidos.csv`);
          if (noRecogidos) results.push(noRecogidos);
        }
        // AFOTOT-a3 (DOP-1 b): el GeoJSON lleva al lado su capa de aforos y albergue.
        if (formato === 'geojson' && result.success) {
          const { GeoJSONExporter } = await import('./GeoJSONExporter');
          const base = this.generateFilename(options.metadata.archivoOrigen);
          const aforos = GeoJSONExporter.exportAforos(options, `${base}_aforos_albergue.geojson`);
          if (aforos) results.push(aforos);
          // ALBRTA-b2d-2 (DOP-2 a de `b2d`): y su capa de alojamientos del registro que el plan no recoge.
          const noRecogidos = GeoJSONExporter.exportNoRecogidosRTA(options, `${base}_alojamientos_no_recogidos.geojson`);
          if (noRecogidos) results.push(noRecogidos);
        }
      } catch (error) {
        results.push({
          success: false,
          filename: '',
          format: formato,
          size: 0,
          blob: new Blob(),
          error: error instanceof Error ? error.message : 'Error desconocido',
        });
      }
    }

    return results;
  }

  /**
   * Exporta datos en un formato específico
   */
  static async exportSingle(
    format: ExportFormat,
    options: ExportOptions
  ): Promise<ExportResult> {
    const baseFilename = this.generateFilename(options.metadata.archivoOrigen);

    switch (format) {
      case 'xlsx':
        const { XLSXExporter } = await import('./XLSXExporter');
        return XLSXExporter.export(options, `${baseFilename}.xlsx`);

      case 'ods':
        const { ODSExporter } = await import('./ODSExporter');
        return ODSExporter.export(options, `${baseFilename}.ods`);

      case 'geojson':
        const { GeoJSONExporter } = await import('./GeoJSONExporter');
        return GeoJSONExporter.export(options, `${baseFilename}.geojson`);

      case 'csv':
        const { CSVExporter } = await import('./CSVExporter');
        return CSVExporter.export(options, `${baseFilename}.csv`);

      case 'kml':
        const { KMLExporter } = await import('./KMLExporter');
        return KMLExporter.export(options, `${baseFilename}.kml`);

      case 'shapefile':
        const { ShapefileExporter } = await import('./ShapefileExporter');
        return ShapefileExporter.export(options, `${baseFilename}_UTM30.zip`);

      case 'html':
        const { HTMLReportExporter } = await import('./HTMLReportExporter');
        return HTMLReportExporter.export(options, `${baseFilename}_informe.html`);

      case 'pdf':
        const { PDFReportExporter } = await import('./PDFReportExporter');
        return PDFReportExporter.export(options, `${baseFilename}_informe.pdf`);

      default:
        throw new Error(`Formato no soportado: ${format}`);
    }
  }

  /**
   * Genera nombre de archivo basado en origen y fecha
   */
  static generateFilename(sourceFile: string): string {
    const rawBase = sourceFile.replace(/\.[^/.]+$/, '');
    const baseName = rawBase.replace(/[^a-zA-Z0-9_.-]/g, '_');
    const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
    return `${baseName}_PTEL_UTM30_${date}`;
  }

  /**
   * Descarga un blob como archivo
   */
  static downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Formatea una fecha como AAAAMMDD_HHMM en hora local (regla de formato PTEL).
   */
  static formatTimestamp(date: Date = new Date()): string {
    const p = (n: number): string => String(n).padStart(2, '0');
    return (
      `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}` +
      `_${p(date.getHours())}${p(date.getMinutes())}`
    );
  }

  /**
   * Nombre del ZIP de lote: {municipio}_{ine5}_{AAAAMMDD_HHMM}.zip
   * Normaliza acentos y sanea a caracteres de nombre de archivo seguros.
   */
  static buildLoteZipFilename(
    municipio: string,
    codigoINE: string,
    date: Date = new Date()
  ): string {
    const safe = (s: string): string =>
      (s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_.-]/g, '_');
    const muni = safe(municipio) || 'Municipio';
    const ine = safe(codigoINE) || '00000';
    return `${muni}_${ine}_${this.formatTimestamp(date)}.zip`;
  }

  /**
   * Empaqueta en un único ZIP los blobs de los resultados dados (clave = filename).
   * Import perezoso de JSZip: reutiliza el chunk `vendor-files` ya existente, no crea
   * un chunk de app nuevo (no amplía OBS-FT-EXPORT-STALE-CHUNK-PRELOADERROR).
   * El shapefile (_UTM30.zip) entra como una entrada más, sin re-empaquetar.
   */
  static async packageBlobsToZip(results: ExportResult[]): Promise<Blob> {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    for (const r of results) {
      zip.file(r.filename, r.blob);
    }
    return zip.generateAsync({ type: 'blob' });
  }

  /**
   * Filtra registros geolocalizados (para GeoJSON/KML)
   */
  static filterGeolocated(records: ProcessedRecord[]): ProcessedRecord[] {
    return records.filter(r => r.geocodificado && r.xUtm30 && r.yUtm30);
  }

  /**
   * Valida opciones de exportación
   */
  static validateOptions(options: ExportOptions): string[] {
    const errors: string[] = [];

    if (!options.formatos || options.formatos.length === 0) {
      errors.push('Debe seleccionar al menos un formato de exportación');
    }

    if (!options.registros || options.registros.length === 0) {
      errors.push('No hay registros para exportar');
    }

    // Verificar formatos que requieren geolocalización
    const geoFormats = options.formatos.filter(f => f === 'geojson' || f === 'kml' || f === 'shapefile');
    if (geoFormats.length > 0) {
      const geoRecords = this.filterGeolocated(options.registros);
      if (geoRecords.length === 0) {
        errors.push(`Los formatos ${geoFormats.join(', ')} requieren registros geolocalizados`);
      }
    }

    return errors;
  }

  /**
   * Calcula tamaño estimado de exportación
   */
  static estimateSize(options: ExportOptions): number {
    const baseSize = 1024; // 1KB base
    const perRecord = 500; // 500 bytes por registro aproximadamente
    return baseSize + (options.registros.length * perRecord * options.formatos.length);
  }
}

export default ExportService;
